import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import InlineErrorMessage from '../../Components/InlineErrorMessage';
import AuthLayout from '../../Components/AuthLayout';
import { authenticateWithPhone } from '../../Services/auth';
import { getProducts } from '../../Services/subscription';
import { findPlanProduct } from '../../Utils/planUtils';
import './style.css';

const PhoneInputPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [planInfo, setPlanInfo] = useState(null);
  
  const planName = searchParams.get('plan');
  const subscriptionMode = searchParams.get('subscription_mode');

  useEffect(() => {
    if (planName && subscriptionMode) {
      fetchPlanInfo();
    }
  }, [planName, subscriptionMode]);

  const fetchPlanInfo = async () => {
    try {
      const response = await getProducts();
      if (response?.payload?.products) {
        const planData = findPlanProduct(response.payload.products, planName, subscriptionMode);
        
        if (planData) {
          setPlanInfo({
            name: planData.displayName,
            price: planData.price,
            interval: planData.interval
          });
        }
      }
    } catch (err) {
      console.error('Error fetching plan info:', err);
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!phoneNumber || isSending) return;

    const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber);
    if (!parsedPhoneNumber) {
      setError('Invalid phone number.');
      return;
    }

    setIsSending(true);
    setError('');

    const countryCode = `+${parsedPhoneNumber.countryCallingCode}`;
    const contact = parsedPhoneNumber.nationalNumber;

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload = {
        contact: contact,
        country_code: countryCode,
        timezone: timezone
      };

      const response = await authenticateWithPhone(payload.contact, payload.country_code, payload.timezone);
      
      if (response.status === 200 && response.payload) {
        // Navigate to OTP page with state
        const otpState = {
          phoneNumber: phoneNumber,
          contact: contact,
          countryCode: countryCode,
          sessionInfo: response.payload.sessionInfo,
          isNewUser: response.payload.isNewUser || false,
          isVerified: response.payload.isVerified,
          authMethod: 'phone'
        };
        
        // Preserve query params
        const queryString = searchParams.toString();
        navigate(`/login/verify-otp?${queryString}`, { state: otpState });
      } else {
        const errorMsg = response.message || 'Failed to send OTP. Please try again.';
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to send OTP. Please try again.';
      setError(errorMsg);
    } finally {
      setIsSending(false);
    }
  };

  const handleBack = () => {
    const queryString = searchParams.toString();
    navigate(`/login${queryString ? '?' + queryString : ''}`);
  };

  return (
    <AuthLayout showBackButton={false} onBack={handleBack}>
      {planInfo && (
        <p className="auth-plan-info">
          You're getting: {planInfo.name} - ${planInfo.price}/{planInfo.interval === 'yearly' ? 'year' : 'month'}
        </p>
      )}
      
      <form onSubmit={handlePhoneSubmit} className="auth-form">
        <h2 className="auth-title">Continue with Mobile</h2>
        <p className="auth-subtitle">Enter your phone number to continue</p>
        
        <div className="auth-input-container">
          <PhoneInput
            international
            defaultCountry="US"
            value={phoneNumber}
            onChange={(value) => {
              setPhoneNumber(value || '');
              setError('');
            }}
            className="auth-phone-input"
            placeholder="(XXX) XXX - XXXX"
          />
        </div>

        <InlineErrorMessage message={error} />

        <button
          type="submit"
          className={`auth-submit-button ${phoneNumber && !isSending ? "enabled" : ""}`}
          disabled={!phoneNumber || isSending}
        >
          {isSending ? 'Sending...' : 'Continue'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default PhoneInputPage;
